import {ButtonHTMLAttributes, ComponentProps} from 'react';
import styles from './styles.module.scss';
import {Link} from 'react-router-dom';

// Constants
const VARIANT_PRIMARY = 'primary';
const VARIANT_SECONDARY = 'secondary';
const VARIANT_SECONDARY2 = 'secondary2';
const VARIANT_GHOST = 'ghost';
const VARIANT_GHOST2 = 'ghost2';
const VARIANT_TERTIARY = 'tertiary';
const VARIANT_GREY2 = 'grey2';
const VARIANT_GREY = 'grey';
const VARIANT_DANGER = 'danger';

export const ButtonVariants = [
  VARIANT_PRIMARY,
  VARIANT_SECONDARY,
  VARIANT_SECONDARY2,
  VARIANT_GHOST,
  VARIANT_GHOST2,
  VARIANT_TERTIARY,
  VARIANT_GREY2,
  VARIANT_GREY,
  VARIANT_DANGER,
] as const;

export type IButtonVariant = (typeof ButtonVariants)[number];

/**
 * md (44px) is the default: page toolbars and CTAs, hero actions, modal
 * footers, empty and error states. sm (36px) is for table row actions, card
 * actions, filters and selects inside a card, and pagination. Never mix the
 * two in one row of controls.
 */
export type IButtonSize = 'md' | 'sm';

type LinkType = ComponentProps<typeof Link> & {
  as: 'link';
};

type ButtonType = ButtonHTMLAttributes<HTMLButtonElement> & {
  as?: 'button';
};

type ButtonProps = (ButtonType | LinkType) & {
  variant?: IButtonVariant;
  fullWidth?: boolean;
  size?: IButtonSize;
  /** A square button holding only an icon: 44x44 (md) or 36x36 (sm). */
  iconOnly?: boolean;
};

const Button = ({
  children,
  fullWidth = false,
  size = 'md',
  iconOnly = false,
  className: callerClassName,
  ...props
}: ButtonProps) => {
  const {variant = 'primary', as} = props;

  // The caller's className is added to the button's own classes, never
  // instead of them, so every button keeps the scale.
  const className = [
    styles.button,
    styles[variant],
    fullWidth && styles.fullWidth,
    as === 'link' && styles.link,
    size === 'sm' && styles.sm,
    iconOnly && styles.iconOnly,
    callerClassName,
  ]
    .filter(Boolean)
    .join(' ');

  if (as === 'link') {
    return (
      <Link {...props} className={className}>
        {children}
      </Link>
    );
  }

  return (
    <button type="button" {...props} className={className}>
      {children}
    </button>
  );
};

export default Button;
